import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useRecipeCatalog from '../../hooks/useRecipeCatalog';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import RecipeBookSummaryBar from './RecipeBookSummaryBar';
import RecipeBookFilters from './RecipeBookFilters';
import RecipeBookCard from './RecipeBookCard';
import RecipeBookDetail from './RecipeBookDetail';
import { TIER_ORDER, tierLabel } from './recipeBookConstants';
import { recipeBookNavButtonClassName, recipeBookPanelClassName } from './recipeBookStyles';

export default function RecipeBookModal({
	isOpen,
	onClose,
	recipeCatalogEndpoint,
	recipeCatalogSuggestEndpoint
}) {
	const { csrfToken } = useAuth();
	const [selectedRecipe, setSelectedRecipe] = useState(null);
	const scrollContainerRef = useRef(null);
	const detailPanelRef = useRef(null);

	const scrollDetailIntoView = useCallback(() => {
		const container = scrollContainerRef.current;
		const detail = detailPanelRef.current;
		if (!container || !detail) return;

		const padding = 12;
		const detailTop = detail.getBoundingClientRect().top;
		const containerTop = container.getBoundingClientRect().top;
		const nextScrollTop = container.scrollTop + (detailTop - containerTop) - padding;

		container.scrollTo({ top: Math.max(0, nextScrollTop), behavior: 'smooth' });
	}, []);

	const handleOpenRecipe = useCallback((recipe) => {
		setSelectedRecipe(recipe);
	}, []);

	useEffect(() => {
		if (!selectedRecipe?.id) return undefined;

		const frame = requestAnimationFrame(() => {
			requestAnimationFrame(scrollDetailIntoView);
		});

		return () => cancelAnimationFrame(frame);
	}, [selectedRecipe?.id, scrollDetailIntoView]);

	const {
		meta,
		recipes,
		recipesByTier,
		pagination,
		loading,
		error,
		filters,
		updateFilter,
		clearFilters,
		hasActiveFilters,
		suggestions,
		suggestOpen,
		setSuggestOpen,
		reload
	} = useRecipeCatalog({
		catalogEndpoint: recipeCatalogEndpoint,
		suggestEndpoint: recipeCatalogSuggestEndpoint,
		csrfToken,
		enabled: isOpen
	});

	const flatForNavigation = useMemo(() => {
		if (hasActiveFilters) return recipes;
		return TIER_ORDER.flatMap((tier) => recipesByTier[tier] || []);
	}, [hasActiveFilters, recipes, recipesByTier]);

	const handleSelectSuggestion = (item) => {
		if (item.type === 'ingredient') {
			updateFilter('ingredient', item.value);
			updateFilter('search', '');
		} else {
			updateFilter('search', item.value);
			updateFilter('ingredient', '');
		}
		setSuggestOpen(false);
	};

	const handleClose = () => {
		clearFilters();
		setSelectedRecipe(null);
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-start justify-center px-4 pb-4 pt-8 md:pt-16 overflow-hidden"
			style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
			role="dialog"
			aria-modal="true"
			aria-labelledby="recipe-book-title"
		>
			<div
				className="relative flex flex-col w-full max-w-4xl min-h-0 bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-xl overflow-hidden shadow-xl"
				style={{ maxHeight: '83vh' }}
			>
				<div className="shrink-0 px-6 pt-6 pb-4 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card">
					<div className="flex items-start justify-between gap-4">
						<div>
							<h2
								id="recipe-book-title"
								className="text-2xl font-bold text-gray-900 dark:text-dark-text"
							>
								Community Cookbook
							</h2>
							<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
								Crowdsourced from Save My Food — only reviewed recipes appear here. Source links
								are shown when verified.
							</p>
						</div>
						<button
							type="button"
							onClick={handleClose}
							className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-3xl leading-none"
							aria-label="Close community cookbook"
						>
							×
						</button>
					</div>
				</div>

				<div className="flex flex-col flex-1 min-h-0 overflow-hidden">
					<div
						ref={scrollContainerRef}
						className="flex-1 min-h-0 overflow-y-auto px-4 py-4 md:px-6 space-y-4"
					>
					<RecipeBookSummaryBar meta={meta} loading={loading && !meta} />

					<RecipeBookFilters
						filters={filters}
						onFilterChange={updateFilter}
						onClearFilters={clearFilters}
						hasActiveFilters={hasActiveFilters}
						suggestions={suggestions}
						suggestOpen={suggestOpen}
						onSuggestOpenChange={setSuggestOpen}
						onSelectSuggestion={handleSelectSuggestion}
					/>

					{error ? (
						<div
							className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/30 dark:text-red-300"
							role="alert"
						>
							{error}
							<button
								type="button"
								className="ml-3 underline"
								onClick={reload}
							>
								Retry
							</button>
						</div>
					) : null}

					{selectedRecipe ? (
						<div ref={detailPanelRef} className="scroll-mt-3">
							<RecipeBookDetail
								recipe={selectedRecipe}
								recipes={flatForNavigation}
								onClose={() => setSelectedRecipe(null)}
								onNavigate={handleOpenRecipe}
							/>
						</div>
					) : null}

					{loading && recipes.length === 0 ? (
						<div className={`flex justify-center py-16 ${recipeBookPanelClassName}`}>
							<LoadingSpinner />
						</div>
					) : recipes.length === 0 && !loading ? (
						<div className={`px-8 py-16 text-center ${recipeBookPanelClassName}`}>
							<p className="text-lg font-medium text-gray-700 dark:text-dark-text">
								No recipes yet
							</p>
							<p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
								Scan food with Save My Food to grow the community cookbook.
							</p>
						</div>
					) : hasActiveFilters ? (
						<div className="space-y-3">
							<p className="text-xs font-medium text-gray-500 dark:text-gray-400 px-1">
								<span className="font-semibold text-gray-800 dark:text-dark-text">
									{pagination?.total ?? recipes.length}
								</span>{' '}
								recipes match
							</p>
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-0.5">
								{recipes.map((recipe) => (
									<RecipeBookCard
										key={recipe.id}
										recipe={recipe}
										onOpen={handleOpenRecipe}
										isActive={selectedRecipe?.id === recipe.id}
									/>
								))}
							</div>
							{pagination && pagination.total_pages > 1 ? (
								<div className="flex justify-center gap-2 pt-2">
									<button
										type="button"
										disabled={pagination.page <= 1}
										className={recipeBookNavButtonClassName}
										onClick={() => updateFilter('page', pagination.page - 1)}
									>
										Previous page
									</button>
									<span className="text-sm text-gray-500 dark:text-gray-400 self-center tabular-nums">
										Page {pagination.page} of {pagination.total_pages}
									</span>
									<button
										type="button"
										disabled={pagination.page >= pagination.total_pages}
										className={recipeBookNavButtonClassName}
										onClick={() => updateFilter('page', pagination.page + 1)}
									>
										Next page
									</button>
								</div>
							) : null}
						</div>
					) : (
						TIER_ORDER.map((tier) => {
							const tierRecipes = recipesByTier[tier] || [];
							if (!tierRecipes.length) return null;
							return (
								<section key={tier} className="space-y-2">
									<h3 className="text-sm font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400 px-1">
										{tierLabel(tier)}
									</h3>
									<div className="flex gap-3 overflow-x-auto py-1 px-0.5 -mx-0.5 snap-x snap-mandatory scrollbar-thin">
										{tierRecipes.map((recipe) => (
											<div key={recipe.id} className="snap-start py-0.5">
												<RecipeBookCard
													recipe={recipe}
													onOpen={handleOpenRecipe}
													isActive={selectedRecipe?.id === recipe.id}
												/>
											</div>
										))}
									</div>
								</section>
							);
						})
					)}
					</div>
				</div>
			</div>
		</div>
	);
}
